import { api } from './axios';

export interface PosPaymentPayload {
  method: 'CASH' | 'TERMINAL';
  amount: number;
}

export interface PosItemPayload {
  product_id: number;
  qty: number;
}

export interface CreatePosTransactionPayload {
  items: PosItemPayload[];
  payments: PosPaymentPayload[];
  location_id: number;
  client_id?: number;
  next_service_days?: number;
  discount_percent?: number;
  discount_amount?: number;
}

export interface OpenShiftPayload {
  location_id: number;
  opening_cash: number;
}

export interface CloseShiftPayload {
  location_id: number;
  closing_cash: number;
}

export interface CashCollectionPayload {
  location_id: number;
  amount: number;
}

export interface PosRefundPayload {
  sale_id: number;
  items: { product_id: number; qty: number }[];
  location_id: number;
  reason?: string;
}

export interface CatalogProduct {
  id: number;
  name_ru: string;
  name_uz: string | null;
  barcode: string | null;
  photo_url: string | null;
  category_id: number | null;
  unit_id: number | null;
  unit: { id: number; name: string; abbreviation: string } | null;
  category: { id: number; name_ru: string } | null;
  prices: { price: string }[];
  stock: { qty_total: number }[];
  tags?: string[];
}

export interface CatalogCategory {
  id: number;
  name_ru: string;
  name_uz: string | null;
}

export interface ShiftSummary {
  active: boolean;
  shift: {
    id: number;
    opened_at: string;
    opening_cash: number;
    closing_cash: number | null;
  } | null;
  summary: {
    total_sales: number;
    total_revenue: number;
    cash_revenue: number;
    terminal_revenue: number;
    avg_check: number;
    sales: {
      id: number;
      total: number;
      created_at: string;
      status: string;
      items?: { product_name: string; qty: number; price: number }[];
    }[];
  } | null;
}

export interface PosLocation {
  id: number;
  name: string;
  type: string;
  branch_id: number;
  account_id: number | null;
}

export interface PosClient {
  id: number;
  name: string;
  phone: string | null;
  car_number: string | null;
  car_brand: { id: number; name: string } | null;
  car_model: { id: number; name: string } | null;
  car_year: number | null;
  created_by_id?: number;
  created_at?: string;
}

export interface CarBrand {
  id: number;
  name: string;
  models: { id: number; name: string }[];
}

export interface PosTransfer {
  id: number;
  status: string;
  created_at: string;
  from_location: { id: number; name: string };
  to_location: { id: number; name: string };
  creator: { name: string };
  items: {
    id: number;
    product_id: number;
    qty: number;
    product: { name_ru: string; unit?: { name: string } | null };
  }[];
}

export interface ReceiptBlock {
  id: string;
  type: string; // header, orderInfo, clientInfo, items, total, footer, customText, qrCode, logo
  enabled: boolean;
  label: string;
  content?: string; // used for customText
  qrData?: string; // used for qrCode
  logoUrl?: string; // used for logo
}

export interface ReceiptTemplate {
  blocks: ReceiptBlock[];
  headerText: string;
  footerText: string;
  showLogo: boolean;
  fontSize: number;
  margins?: { top: number; right: number; bottom: number; left: number };
}

export const posApi = {
  // --- Clients ---
  getMyClients: async () => {
    const res = await api.get('/pos/clients');
    return res.data as PosClient[];
  },

  searchClients: async (query: string) => {
    const res = await api.get('/pos/clients/search', { params: { q: query } });
    return res.data as PosClient[];
  },

  createClient: async (data: {
    name: string;
    phone?: string;
    car_number?: string;
    car_brand_id?: number;
    car_model_id?: number;
    car_year?: number;
  }) => {
    const res = await api.post('/pos/clients', data);
    return res.data as PosClient;
  },

  getClientHistory: async (clientId: number) => {
    const res = await api.get(`/pos/clients/${clientId}/history`);
    return res.data;
  },

  getCarBrands: async () => {
    const res = await api.get('/pos/car-brands');
    return res.data as CarBrand[];
  },

  // --- Receipt Settings ---
  getReceiptSettings: async () => {
    const res = await api.get('/pos/receipt-settings');
    return res.data as { template: ReceiptTemplate; branchName: string };
  },

  saveReceiptSettings: async (template: ReceiptTemplate) => {
    const res = await api.post('/pos/receipt-settings', template);
    return res.data;
  },

  // --- Transfers ---
  getTransfersByLocation: async (locationId: number, direction?: 'incoming' | 'outgoing') => {
    const res = await api.get(`/transfers/by-location/${locationId}`, { params: { direction } });
    return res.data as { data: PosTransfer[]; pendingIncoming: number };
  },

  receiveTransfer: async (id: number) => {
    const res = await api.post(`/transfers/${id}/receive`);
    return res.data;
  },

  createTransfer: async (data: {
    from_location_id: number;
    to_location_id: number;
    items: { product_id: number; qty: number }[];
  }) => {
    const res = await api.post('/transfers', data);
    return res.data;
  },

  submitTransfer: async (id: number) => {
    const res = await api.post(`/transfers/${id}/submit`);
    return res.data;
  },

  shipTransfer: async (id: number) => {
    const res = await api.post(`/transfers/${id}/ship`);
    return res.data;
  },

  // --- Locations ---
  getLocations: async () => {
    const response = await api.get('/pos/locations');
    return response.data as PosLocation[];
  },

  checkout: async (data: CreatePosTransactionPayload) => {
    const response = await api.post('/pos/checkout', data);
    return response.data;
  },

  verifyPin: async (pin: string) => {
    const response = await api.post('/pos/pin/verify', { pin });
    return response.data;
  },

  getCatalog: async (locationId: number) => {
    const response = await api.get(`/pos/catalog/${locationId}`);
    return response.data as { categories: CatalogCategory[]; products: CatalogProduct[] };
  },

  checkShift: async (locationId: number) => {
    const response = await api.get(`/pos/shifts/check/${locationId}`);
    return response.data;
  },

  getShiftSummary: async (locationId: number) => {
    const response = await api.get(`/pos/shifts/summary/${locationId}`);
    return response.data as ShiftSummary;
  },

  openShift: async (data: OpenShiftPayload) => {
    const response = await api.post('/pos/shifts/open', data);
    return response.data;
  },

  closeShift: async (data: CloseShiftPayload) => {
    const response = await api.post('/pos/shifts/close', data);
    return response.data;
  },

  cashCollection: async (data: CashCollectionPayload) => {
    const response = await api.post('/pos/finance/collection', data);
    return response.data;
  },

  refund: async (data: PosRefundPayload) => {
    const response = await api.post('/pos/refund', data);
    return response.data;
  },

  getReceiptUrl: (saleId: number) => {
    let token = localStorage.getItem('erp_access_token') || '';
    if (token.startsWith('"') && token.endsWith('"')) {
      token = token.slice(1, -1);
    }
    // Electron's BrowserWindow.loadURL() requires an absolute URL (with protocol+host).
    // api.defaults.baseURL is typically relative ("/api/v1"), so we prepend window.location.origin.
    const baseUrl = api.defaults.baseURL?.startsWith('http')
      ? api.defaults.baseURL
      : `${window.location.origin}${api.defaults.baseURL}`;
    return `${baseUrl}/pos/receipt/${saleId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  },

  getShiftHistory: async (params?: { location_id?: number; page?: number; limit?: number }) => {
    const response = await api.get('/pos/shifts/history', { params });
    return response.data;
  },
};
